import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  type ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideTranslateService, MissingTranslationHandler, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NZ_I18N, en_US, fr_FR } from 'ng-zorro-antd/i18n';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import { provideCharts } from 'ng2-charts';
import {
  ArcElement,
  CategoryScale,
  DoughnutController,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js';
import { appRoutes } from './app.routes';
import { requestActivityInterceptor } from './core/interceptors/request-activity.interceptor';
import { authorizationInterceptor } from './core/interceptors/authorization.interceptor';
import { errorReportingInterceptor } from './core/interceptors/error-reporting.interceptor';
import { provideRumService } from './shared/services/rum/rum.service';
import { abortErrorFilter } from './core/http-error/http-error-default-filters';
import { HttpErrorHandlerService } from './core/http-error/http-error-handler.service';
import { provideHttpErrorFilter } from './core/http-error/http-error-reporter.service';
import { ChunkReloadErrorHandler } from './shared/error-handlers/chunk-reload-error-handler';
import { MissingTranslationReloadHandler } from './shared/error-handlers/missing-translation-reload-handler';

const LOCALE_DATA_MAP = {
  en: localeEn,
  fr: localeFr,
};

const localeSetupFactory = (translateService: TranslateService): string => {
  const lang = (translateService.getBrowserLang() as keyof typeof LOCALE_DATA_MAP) || 'en';
  const localeData = LOCALE_DATA_MAP[lang] ?? LOCALE_DATA_MAP['en'];
  registerLocaleData(localeData, lang);
  return translateService.getBrowserCultureLang() || 'en-US';
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      // Angular 22 default changed; keep parent-param inheritance behaviour
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
    ),
    provideHttpClient(withInterceptors([
      // reporting should be early in the chain so it sees final outcomes
      errorReportingInterceptor,
      authorizationInterceptor,
      requestActivityInterceptor,
    ])),
    provideCharts({
      defaults: {
        plugins: {
          tooltip: { enabled: false },
          legend: { display: false },
        },
      },
      registerables: [
        DoughnutController,
        ArcElement,
        LineController,
        CategoryScale,
        LinearScale,
        TimeScale,
        PointElement,
        LineElement,
        Tooltip,
      ],
    }),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationReloadHandler,
      },
    }),
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
    ),
    provideAnimations(),
    // ng-zorro 22 no longer provides a default date adapter
    provideNzDateFnsAdapter(),
    provideRumService(),
    provideHttpErrorFilter(abortErrorFilter),
    {
      // instantiate ErrorHandlerService at app startup so that
      // constructor etc are registered before any HTTP activity.
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        inject(HttpErrorHandlerService);
        return () => undefined;
      },
    },
    { provide: ErrorHandler, useClass: ChunkReloadErrorHandler },
    {
      provide: LOCALE_ID,
      useFactory: localeSetupFactory,
      deps: [TranslateService],
    },
    {
      provide: NZ_I18N,
      useFactory: () => {
        const localId = inject(LOCALE_ID);
        switch (localId) {
          case 'fr-FR':
            return fr_FR;
          case 'en-US':
          default:
            return en_US;
        }
      },
    },
  ],
};
